import React, { useContext, useEffect, useState } from 'react';
import ContextSnackbar from 'Components/Snackbar';
import { HeadingTwo } from 'Components/Headings';
import { subRouteCodes as SUB_ROUTES } from 'Constants/routes';
import AddButton from 'Components/Button/AddButton';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { displayFlex } from '../../../styles/Mixins';
import { paragraphReg } from '../../../styles/Typography';
import colors from '../../../styles/Colors';
import './Users.scss';
import { useQuery, useMutation, useLazyQuery } from '@apollo/react-hooks';
import { VENUES } from 'Queries/SuperAdmin';
import { USERS_SEARCH } from 'Queries/SuperAdmin/UsersSearch';
import { UPSERT_USER } from 'Mutations/SuperAdmin/UpsertUser';
import { MenuItem, Select } from '@material-ui/core';
import { Input, Button, Space, Descriptions, message, Table } from 'antd';
import { SearchOutlined, EditOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { SnackbarContext } from 'Store/SnackbarContext';

const getOptions = data => {
  if (!data) return null;

  const options = data.map(data => (
    <MenuItem key={data.id} value={data.id}>
      ID: {data.id} - {data.name}
    </MenuItem>
  ));

  return options;
};

function Users(props) {
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [searchedUser, setSearchedUser] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [editableFields, setEditableFields] = useState({});
  const [messageApi, contextHolder] = message.useMessage();

  const { data: venuesData, loading: venuesLoading, error: venuesError } = useQuery(VENUES, {
    fetchPolicy: 'network-only'
  });

  const [searchUsers, { data: userData, loading: userLoading }] = useLazyQuery(USERS_SEARCH, {
    fetchPolicy: 'network-only'
  });

  const { showSnackbar } = useContext(SnackbarContext);

  const [upsertUser, { loading: upsertLoading }] = useMutation(UPSERT_USER, {
    onCompleted: data => {
      if (data.upsertUser.error) {
        console.error(data.upsertUser.error);
        showSnackbar('There was an error updating the user', {
          duration: 3000,
          error: true
        });
      } else {
        showSnackbar('User updated successfully', {
          duration: 3000,
          error: false
        });
        setEditableFields({});
        handleClear();
      }
    },
    onError: error => {
      console.error(error);
      showSnackbar('There was an error updating the user', {
        duration: 3000,
        error: true
      });
    }
  });

  const { className } = props;

  useEffect(() => {
    if (venuesData) setVenues(venuesData.venues.sort((a, b) => +a.id - +b.id));
  }, [venuesData]);

  useEffect(() => {
    if (userData && userData.users) {
      if (userData.users.length > 0) {
        setSearchResults(userData.users);
        setSearchedUser(null); // Clear any previously selected user
        setEditableFields({});
      } else {
        messageApi.open({
          type: 'warning',
          content: 'No user found with the provided criteria'
        });
        setSearchResults([]);
        setSearchedUser(null);
      }
    }
  }, [userData, messageApi]);

  const handleSelectUser = user => {
    setSearchedUser(user);
  };

  const handleBackToList = () => {
    setSearchedUser(null);
  };

  const handleVenueChange = e => {
    const venue = venues.filter(venue => venue.id === e.target.value)[0];
    setSelectedVenue(venue);
  };

  const handleSearch = () => {
    if (!firstName && !lastName && !email) {
      messageApi.open({
        type: 'warning',
        content: 'Please enter at least one search criteria'
      });
      return;
    }
    searchUsers({
      variables: {
        filterBy: {
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          email: email || undefined
        }
      }
    });
  };

  const handleClear = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setSearchedUser(null);
    setSearchResults([]);
    setEditableFields({});
  };

  const toggleEditable = field => {
    setEditableFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleFieldChange = (field, value) => {
    setSearchedUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    if (!searchedUser) return;

    const input = {
      id: searchedUser.id,
      email: searchedUser.email,
      firstName: searchedUser.firstName,
      lastName: searchedUser.lastName,
      phone: searchedUser.phone,
      street: searchedUser.street,
      street2: searchedUser.street2,
      city: searchedUser.city,
      state: searchedUser.state,
      zip: searchedUser.zip
    };

    upsertUser({
      variables: { input }
    });
  };

  return (
    <>
      <div className={className}>
        <ContextSnackbar />
        {contextHolder}
        <div className={`${className}__page-header`}>
          <div className={`${className}__header-wrapper`}>
            <HeadingTwo label="USERS" />
          </div>
        </div>
      </div>
      <div className={className}>
        <div className={`${className}__page-body`}>
          <div>
            <CreateUserLink to={SUB_ROUTES.SUPER_ADMIN.USER_CREATE}>
              <AddButton label={'CREATE USER'} />
            </CreateUserLink>
          </div>

          {/* User Search Section */}
          <div className={`${className}__search-section`}>
            <h3>Search User</h3>
            <div className={`${className}__search-form`}>
              <Space>
                <Input placeholder="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} style={{ width: 200 }} />
                <Input placeholder="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} style={{ width: 200 }} />
                <Input placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} style={{ width: 200 }} />
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={userLoading}>
                  Search
                </Button>
                <Button onClick={handleClear}>Clear</Button>
              </Space>
            </div>

            {/* User Details Section */}
            <div className={`${className}__user-details`}>
              {searchResults.length > 0 && !searchedUser && (
                <div className={`${className}__user-list`}>
                  <h3>Search Results</h3>
                  <Table
                    dataSource={searchResults.map(user => ({ ...user, key: user.id }))}
                    columns={[
                      {
                        title: 'USER ID (not SSUser)',
                        dataIndex: 'id',
                        key: 'id'
                      },
                      {
                        title: 'First Name',
                        dataIndex: 'firstName',
                        key: 'firstName'
                      },
                      {
                        title: 'Last Name',
                        dataIndex: 'lastName',
                        key: 'lastName'
                      },
                      {
                        title: 'Email',
                        dataIndex: 'email',
                        key: 'email'
                      }
                    ]}
                    pagination={false}
                    style={{ marginTop: 20, marginBottom: 20 }}
                    onRow={record => ({
                      onClick: () => handleSelectUser(record),
                      style: { cursor: 'pointer' }
                    })}
                  />
                </div>
              )}

              {searchedUser && (
                <div>
                  <Button icon={<ArrowLeftOutlined />} onClick={handleBackToList} style={{ marginBottom: 10 }}>
                    Back to List
                  </Button>
                  <Descriptions title="User Details" bordered column={1} size="small" style={{ marginTop: 10, marginBottom: 20 }}>
                    <Descriptions.Item label="USER ID (Not SSUser)">{searchedUser.id}</Descriptions.Item>
                    <Descriptions.Item label="Email">
                      {editableFields.email ? (
                        <Input
                          value={searchedUser.email}
                          onChange={e => handleFieldChange('email', e.target.value)}
                          onPressEnter={() => toggleEditable('email')}
                          onBlur={() => toggleEditable('email')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {searchedUser.email}
                          <EditOutlined onClick={() => toggleEditable('email')} style={{ cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="First Name">
                      {editableFields.firstName ? (
                        <Input
                          value={searchedUser.firstName}
                          onChange={e => handleFieldChange('firstName', e.target.value)}
                          onPressEnter={() => toggleEditable('firstName')}
                          onBlur={() => toggleEditable('firstName')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {searchedUser.firstName}
                          <EditOutlined onClick={() => toggleEditable('firstName')} style={{ cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Name">
                      {editableFields.lastName ? (
                        <Input
                          value={searchedUser.lastName}
                          onChange={e => handleFieldChange('lastName', e.target.value)}
                          onPressEnter={() => toggleEditable('lastName')}
                          onBlur={() => toggleEditable('lastName')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {searchedUser.lastName}
                          <EditOutlined onClick={() => toggleEditable('lastName')} style={{ cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Phone">
                      {editableFields.phone ? (
                        <Input
                          value={searchedUser.phone}
                          onChange={e => handleFieldChange('phone', e.target.value)}
                          onPressEnter={() => toggleEditable('phone')}
                          onBlur={() => toggleEditable('phone')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {searchedUser.phone}
                          <EditOutlined onClick={() => toggleEditable('phone')} style={{ cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Street">
                      {editableFields.street ? (
                        <Input
                          value={searchedUser.street}
                          onChange={e => handleFieldChange('street', e.target.value)}
                          onPressEnter={() => toggleEditable('street')}
                          onBlur={() => toggleEditable('street')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {searchedUser.street}
                          <EditOutlined onClick={() => toggleEditable('street')} style={{ cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Street 2">
                      {editableFields.street2 ? (
                        <Input
                          value={searchedUser.street2}
                          onChange={e => handleFieldChange('street2', e.target.value)}
                          onPressEnter={() => toggleEditable('street2')}
                          onBlur={() => toggleEditable('street2')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {searchedUser.street2}
                          <EditOutlined onClick={() => toggleEditable('street2')} style={{ cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="City">
                      {editableFields.city ? (
                        <Input
                          value={searchedUser.city}
                          onChange={e => handleFieldChange('city', e.target.value)}
                          onPressEnter={() => toggleEditable('city')}
                          onBlur={() => toggleEditable('city')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {searchedUser.city}
                          <EditOutlined onClick={() => toggleEditable('city')} style={{ cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="State">
                      {editableFields.state ? (
                        <Input
                          value={searchedUser.state}
                          onChange={e => handleFieldChange('state', e.target.value)}
                          onPressEnter={() => toggleEditable('state')}
                          onBlur={() => toggleEditable('state')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {searchedUser.state}
                          <EditOutlined onClick={() => toggleEditable('state')} style={{ cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="ZIP">
                      {editableFields.zip ? (
                        <Input
                          value={searchedUser.zip}
                          onChange={e => handleFieldChange('zip', e.target.value)}
                          onPressEnter={() => toggleEditable('zip')}
                          onBlur={() => toggleEditable('zip')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {searchedUser.zip}
                          <EditOutlined onClick={() => toggleEditable('zip')} style={{ cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Role">
                      <span>{searchedUser.role?.name || 'No role assigned'}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Venues">
                      {searchedUser.venues && searchedUser.venues.length > 0 ? (
                        <div>
                          {searchedUser.venues.map(venue => (
                            <div key={venue.id}>
                              {venue.id} - {venue.name}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span>No venues assigned</span>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                  <Button type="primary" onClick={handleSave} loading={upsertLoading} style={{ marginBottom: 20 }}>
                    Save
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3>Choose a venue</h3>
            {venues && !venuesLoading && !venuesError && (
              <Select data-testid="map-select-venue" onChange={handleVenueChange} value={selectedVenue?.id || ''} disableUnderline>
                {getOptions(venues)}
              </Select>
            )}
            {selectedVenue && <div>USERS LIST (TODO)</div>}
          </div>
        </div>
      </div>
    </>
  );
}

const CreateUserLink = styled(Link)`
  text-decoration: none;
`;

const SuperAdminUsers = styled(Users)`
  &&& {
    ${displayFlex}
    align-items: center;
    width: 100%;
    height: auto;
    padding: 12px 0 11px 20px;
    ${paragraphReg}
    font-size: 14px;
    text-align: left;
    background-color: ${colors.background.primary};
    color: ${colors.text};
    border-radius: 3px;
  }

  &__page-header {
    text-align: left;
    width: 100%
    border-bottom: 1px solid rgb(200, 214, 229);
    &&& {
      margin: 65px 0 25px;
    }

  }

  &__page-body {
    text-align: left;
    width: 100%
    &&& {
      margin: 0;
      padding: 10px
    }

    h3 {
      text-transform: uppercase;
      font-family: IBMPlexSans-Bold;
      font-size: 12px;
      font-weight: 400;
      color: rgb(79, 93, 109);
      margin-top: 0px;
      margin-bottom: 3px;
    }
  }

  &__page-body-child {
    margin-top: 20px;
  }

  &__header-wrapper {
    margin: 20px 0px !important;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .MuiInputBase-root {
    border: ${colors.text.lightGray2} 1px solid;
    border-radius: 3px;
    padding: 0 10px;
    min-width: 300px;
  }

  .MuiSvgIcon-root {
    width: .8em;
    height: .8em;
  }

  .MuiIconButton-root {
    padding-top: 6px;
  }

  .line {
    background: ${colors.border.primary};
    height: 1px;
    flex: 1;
    position: relative;
    top: 11px;
  }

  /* Fix for Descriptions component layout */
  .ant-descriptions-item-label {
    width: 200px;
    height: 50px !important;
    vertical-align: middle;
  }

  .ant-descriptions-item-content {
    position: relative;
    min-height: 50px;
    display: flex;
    align-items: center;
  }

  .ant-descriptions-item-content span {
    display: flex;
    align-items: center;
    min-height: 32px; /* Match Input height */
  }

  .anticon-edit {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

export default SuperAdminUsers;
